import { useMutation, useQuery } from '@tanstack/react-query';
import { getDestinationOptionManifest, listDestinationOptions, validateDestinationOptionSettings } from '../api';
import type { DestinationOption } from '..';
import { keyBy } from 'lodash-es';

/** Shared query for the destination options list; the map hook adds a select. */
function destinationOptionsQuery(workspaceId: number | null) {
  return {
    queryKey: ['destinationOptions', { workspaceId }],
    queryFn: async () => {
      if (workspaceId == null) {
        return Promise.reject();
      }
      return listDestinationOptions(workspaceId);
    },
    enabled: workspaceId != null,
    staleTime: 60 * 1000, // one minute
  };
}

export const useListDestinationOptions = (workspaceId: number | null) => {
  return useQuery(destinationOptionsQuery(workspaceId));
};

export const useListDestinationOptionsMap = (workspaceId: number | null) => {
  return useQuery({
    ...destinationOptionsQuery(workspaceId),
    select: (options: DestinationOption[]) => {
      return keyBy(options, 'option_id');
    },
  });
};

export const useGetDestinationOptionManifest = (
  workspaceId: number | null,
  destinationOptionId: string | null | undefined,
) => {
  return useQuery({
    queryKey: ['destinationOptions', 'manifest', { id: destinationOptionId, workspaceId }],
    queryFn: async () => {
      if (workspaceId == null || destinationOptionId == null) {
        return Promise.reject();
      }
      return getDestinationOptionManifest(workspaceId, destinationOptionId);
    },
    enabled: workspaceId != null && destinationOptionId != null,
    staleTime: 60 * 1000, // one minute
  });
};

export const useValidateDestinationOptionSettings = () => {
  return useMutation({
    mutationFn: ({
      workspaceId,
      optionId,
      settings,
    }: {
      workspaceId: number;
      optionId: string;
      // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
      settings: Record<string, any>;
    }) => {
      if (workspaceId == null || optionId == null) {
        return Promise.reject();
      }

      return validateDestinationOptionSettings(workspaceId, optionId, settings);
    },
  });
};
